import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Icon } from "@/libraries/wpl/foundations/src";

import {
    CustomerStoryFilterObject,
    FilterCategories,
    FilterOptions,
    LocalizedFilterOptions,
    isContentPlatformFacetAvailable
} from "../utils";

import { CustomerStoryFilterSliderFilter } from ".";

interface CustomerStoryFilterModalRadioButtonProps {
    index: number;
    filterCategory: FilterCategories;
    subFilterName: string;
    grouping?: string;
    active: boolean;
    isSubFilterHidden: boolean;
    updateFilterObject: (
        filter: FilterCategories,
        subFilterName: string
    ) => void;
}

const actualInputStyle = css({
    position: "absolute",
    zIndex: -1,
    opacity: 0
});

export const CustomerStoryFilterModalRadioButton = ({
    index,
    filterCategory,
    subFilterName,
    grouping,
    active,
    isSubFilterHidden,
    updateFilterObject
}: CustomerStoryFilterModalRadioButtonProps) => {
    const localizedMap = LocalizedFilterOptions();

    const optionId = `option-${(grouping ?? filterCategory).replace(
        /\s/g,
        ""
    )}-${index}`;
    return (
        <label
            htmlFor={optionId}
            className={`filter-radio-button-container ${
                isSubFilterHidden ? "hide-subfilter" : ""
            }`}
        >
            <input
                id={optionId}
                type="checkbox"
                onClick={() =>
                    updateFilterObject(filterCategory, subFilterName)
                }
                css={actualInputStyle}
            />
            <span className={active ? "checked" : ""}>
                <Icon name="featurecheckmark" size="xs" color="#FFFFFF" />
            </span>
            {isContentPlatformFacetAvailable(grouping ?? filterCategory)
                ? subFilterName
                : localizedMap[subFilterName]}
        </label>
    );
};

export interface CustomerStoryFilterModalComplexGroupingProps {
    filterCategory: FilterCategories;
    filterObject: CustomerStoryFilterObject;
    updateFilterObject: (subFilterName: string) => void;
}

export const CustomerStoryFilterModalComplexGrouping = ({
    filterCategory,
    filterObject,
    updateFilterObject
}: CustomerStoryFilterModalComplexGroupingProps) => {
    const intl = useIntl();
    const sliderFilterContainer: JSX.Element[] = [];
    const regionFilterContainer: JSX.Element[] = [];
    const teamSizeFilterContainer: JSX.Element[] = [];

    Object.keys(filterObject[filterCategory]).map((subFilter, index) => {
        const subFilterObject = filterObject[filterCategory][
            subFilter
        ] as FilterOptions;
        if (subFilterObject.description) {
            sliderFilterContainer.push(
                <CustomerStoryFilterSliderFilter
                    key={subFilter}
                    subFilterName={subFilter}
                    active={subFilterObject.stagedActive}
                    updateFilterObject={() => updateFilterObject(subFilter)}
                    subFilterDescription={subFilterObject.description}
                />
            );
        } else {
            if (subFilterObject.grouping === "region") {
                regionFilterContainer.push(
                    <CustomerStoryFilterModalRadioButton
                        key={index}
                        index={index}
                        filterCategory={filterCategory}
                        subFilterName={subFilter}
                        grouping={subFilterObject.grouping}
                        active={subFilterObject.stagedActive}
                        isSubFilterHidden={false}
                        updateFilterObject={() => updateFilterObject(subFilter)}
                    />
                );
            } else {
                teamSizeFilterContainer.push(
                    <CustomerStoryFilterModalRadioButton
                        key={index}
                        index={index}
                        filterCategory={filterCategory}
                        subFilterName={subFilter}
                        grouping={subFilterObject.grouping}
                        active={subFilterObject.stagedActive}
                        isSubFilterHidden={false}
                        updateFilterObject={() => updateFilterObject(subFilter)}
                    />
                );
            }
        }
    });

    return (
        <div className="filter-modal-body-more-filters">
            {sliderFilterContainer.map((sliderFilter) => {
                return sliderFilter;
            })}
            <div
                css={css({
                    width: "100%",
                    borderBlockEnd: "1px dashed #DDDEE1",
                    paddingBlockEnd: "24px"
                })}
            >
                <h4>
                    {intl.formatMessage({
                        id: "CustomersLandingPage.filterGrouping.region",
                        defaultMessage: "Region"
                    })}
                </h4>
                <div className="filter-modal-grouping-container">
                    {/* Region only has 4 values, so all of them will be in the first column */}
                    <div className="filter-modal-grouping-column">
                        {regionFilterContainer.map((regionFilter) => {
                            return regionFilter;
                        })}
                    </div>
                </div>
            </div>
            <div css={css({ marginBlockStart: "24px", width: "100%" })}>
                <h4>
                    {intl.formatMessage({
                        id: "CustomersLandingPage.filterGrouping.TeamSize",
                        defaultMessage: "Team size"
                    })}
                </h4>
                <div className="filter-modal-grouping-container">
                    {/* Team size only has 3 values, so all of them will be in the first column */}
                    <div className="filter-modal-grouping-column">
                        {teamSizeFilterContainer.map((regionFilter) => {
                            return regionFilter;
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
